import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { NotificationsManagerService } from '../../../notifications/services/notifications-manager.service';
import { AuthenticationManager } from '../../services/authentication-manager';

interface PermissionStatus {
  notifications: NotificationPermission | 'unsupported';
  microphone: PermissionState | 'unsupported';
  camera: PermissionState | 'unsupported';
  microphoneNotFound: boolean;
  cameraNotFound: boolean;
}

@Component({
  selector: 'app-permissions-banner',
  templateUrl: './permissions-banner.component.html',
  styleUrl: './permissions-banner.component.scss'
})
export class PermissionsBannerComponent implements OnInit, OnDestroy {

  private readonly DISMISS_KEY = 'permissions_banner_dismissed';
  private readonly DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

  private authSubscription: Subscription | null = null;
  private routerSubscription: Subscription | null = null;
  private deviceChangeHandler: (() => void) | null = null;
  private isLoggedIn = false;
  private isOnAppInfoPage = false;

  permissions: PermissionStatus = {
    notifications: 'default',
    microphone: 'prompt',
    camera: 'prompt',
    microphoneNotFound: false,
    cameraNotFound: false
  };

  dismissed = false;
  visible = false;
  isSafari = false;
  isEdge = false;
  isStandalone = false;

  constructor(
    private notificationsManager: NotificationsManagerService,
    private auth: AuthenticationManager,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.detectSafari();
    this.checkDismissed();

    this.isOnAppInfoPage = this.router.url.includes('/application/info');
    this.routerSubscription = this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd)
    ).subscribe(e => {
      this.isOnAppInfoPage = e.urlAfterRedirects.includes('/application/info');
      this.updateVisibility();
    });

    this.authSubscription = this.auth.userLoggedInUpdates().subscribe(loggedIn => {
      this.isLoggedIn = loggedIn;
      if (loggedIn) {
        this.checkPermissions();
      } else {
        this.visible = false;
      }
    });

    // Re-check device availability when devices connect/disconnect (e.g. standby wake)
    if (navigator.mediaDevices) {
      this.deviceChangeHandler = () => this.recheckDevices();
      navigator.mediaDevices.addEventListener('devicechange', this.deviceChangeHandler);
    }
  }

  ngOnDestroy(): void {
    this.authSubscription?.unsubscribe();
    this.routerSubscription?.unsubscribe();
    if (this.deviceChangeHandler) {
      navigator.mediaDevices?.removeEventListener('devicechange', this.deviceChangeHandler);
    }
  }

  private detectSafari(): void {
    const ua = navigator.userAgent;
    this.isSafari = /Safari/.test(ua) && !/Chrome/.test(ua) && !/Chromium/.test(ua);
    this.isEdge = /Edg\//.test(ua);
    this.isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || (navigator as any).standalone === true;
  }

  get showSafariDockHint(): boolean {
    return this.isSafari && !this.isStandalone && this.permissions.notifications !== 'unsupported';
  }

  private checkDismissed(): void {
    const dismissedAt = localStorage.getItem(this.DISMISS_KEY);
    if (dismissedAt) {
      const dismissedTime = parseInt(dismissedAt, 10);
      if (Date.now() - dismissedTime < this.DISMISS_DURATION_MS) {
        this.dismissed = true;
      } else {
        localStorage.removeItem(this.DISMISS_KEY);
      }
    }
  }

  private async checkPermissions(): Promise<void> {
    // Check notification permission
    if (typeof Notification === 'undefined') {
      this.permissions.notifications = 'unsupported';
    } else {
      this.permissions.notifications = Notification.permission;
    }

    // Check microphone permission
    if (navigator.permissions) {
      try {
        const micPermission = await navigator.permissions.query({ name: 'microphone' as PermissionName });
        this.permissions.microphone = micPermission.state;
        micPermission.onchange = () => {
          this.permissions.microphone = micPermission.state;
          this.updateVisibility();
        };
        // If permission is already granted, check if a mic device actually exists
        if (micPermission.state === 'granted') {
          await this.checkDeviceExists('audioinput', 'microphoneNotFound');
        }
      } catch {
        this.permissions.microphone = 'unsupported';
      }
    } else {
      this.permissions.microphone = 'unsupported';
    }

    // Check camera permission
    if (navigator.permissions) {
      try {
        const camPermission = await navigator.permissions.query({ name: 'camera' as PermissionName });
        this.permissions.camera = camPermission.state;
        camPermission.onchange = () => {
          this.permissions.camera = camPermission.state;
          this.updateVisibility();
        };
        // If permission is already granted, check if a camera device actually exists
        if (camPermission.state === 'granted') {
          await this.checkDeviceExists('videoinput', 'cameraNotFound');
        }
      } catch {
        this.permissions.camera = 'unsupported';
      }
    } else {
      this.permissions.camera = 'unsupported';
    }
    this.updateVisibility();
  }

  private updateVisibility(): void {
    if (!this.isLoggedIn || (this.dismissed && !this.isOnAppInfoPage)) {
      this.visible = false;
      return;
    }

    const needsNotifications = this.permissions.notifications === 'default';
    const needsMicrophone = this.permissions.microphone === 'prompt';
    const needsCamera = this.permissions.camera === 'prompt';
    const hasDeviceIssue = this.permissions.microphoneNotFound || this.permissions.cameraNotFound;
    const hasDenied = this.permissions.microphone === 'denied' || this.permissions.camera === 'denied' || this.permissions.notifications === 'denied';

    this.visible = needsNotifications || needsMicrophone || needsCamera || hasDeviceIssue || hasDenied || this.showSafariDockHint;
  }

  get needsAnyPermission(): boolean {
    return this.permissions.notifications === 'default'
      || this.permissions.microphone === 'prompt'
      || this.permissions.camera === 'prompt';
  }

  get showNotificationButton(): boolean {
    return this.permissions.notifications === 'default';
  }

  get showMicrophoneButton(): boolean {
    return this.permissions.microphone === 'prompt';
  }

  get notificationsDenied(): boolean {
    return this.permissions.notifications === 'denied';
  }

  get microphoneDenied(): boolean {
    return this.permissions.microphone === 'denied';
  }

  get showCameraButton(): boolean {
    return this.permissions.camera === 'prompt';
  }

  get cameraDenied(): boolean {
    return this.permissions.camera === 'denied';
  }

  get microphoneNotFound(): boolean {
    return this.permissions.microphoneNotFound;
  }

  get cameraNotFound(): boolean {
    return this.permissions.cameraNotFound;
  }

  showEdgeHint = false;

  async enableNotifications(): Promise<void> {
    console.log('[PermissionsBanner] enableNotifications() called');
    console.log('[PermissionsBanner] Notification defined:', typeof Notification !== 'undefined');
    console.log('[PermissionsBanner] Current Notification.permission:', typeof Notification !== 'undefined' ? Notification.permission : 'N/A');

    if (typeof Notification === 'undefined') {
      console.warn('[PermissionsBanner] Notification API not available, aborting');
      return;
    }

    try {
      console.log('[PermissionsBanner] Calling Notification.requestPermission()...');

      // Edge with "quiet notifications" may never resolve requestPermission().
      // Use a timeout to detect this and show the hint.
      const permission = await Promise.race([
        Notification.requestPermission(),
        new Promise<'timeout'>(resolve => setTimeout(() => resolve('timeout'), 3000))
      ]);

      console.log('[PermissionsBanner] requestPermission() resolved with:', permission);

      if (permission === 'granted') {
        console.log('[PermissionsBanner] Permission granted, calling completeNotificationSetup()');
        this.permissions.notifications = 'granted';
        this.showEdgeHint = false;
        this.notificationsManager.completeNotificationSetup();
      } else if (permission === 'timeout' || (permission === 'default' && this.isEdge)) {
        // Edge "quiet notifications" silently blocked the request
        console.log('[PermissionsBanner] Edge quiet notification detected, showing hint');
        this.showEdgeHint = true;
      } else {
        this.permissions.notifications = permission as NotificationPermission;
      }
    } catch (err) {
      console.error('[PermissionsBanner] requestPermission() threw error:', err);
      if (this.isEdge) {
        this.showEdgeHint = true;
      }
    }
    this.updateVisibility();
  }

  async enableMicrophone(): Promise<void> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());
      this.permissions.microphone = 'granted';
      this.permissions.microphoneNotFound = false;
    } catch (err: any) {
      // Re-check actual permission state: if permission was granted but
      // getUserMedia still failed, the device is physically missing.
      const actualState = await this.queryPermissionState('microphone');
      if (actualState === 'granted' || err?.name === 'NotFoundError' || err?.name === 'OverconstrainedError') {
        this.permissions.microphone = 'granted';
        this.permissions.microphoneNotFound = true;
      } else {
        this.permissions.microphone = 'denied';
      }
    }
    this.updateVisibility();
  }

  async enableCamera(): Promise<void> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach(track => track.stop());
      this.permissions.camera = 'granted';
      this.permissions.cameraNotFound = false;
    } catch (err: any) {
      const actualState = await this.queryPermissionState('camera');
      if (actualState === 'granted' || err?.name === 'NotFoundError' || err?.name === 'OverconstrainedError') {
        this.permissions.camera = 'granted';
        this.permissions.cameraNotFound = true;
      } else {
        this.permissions.camera = 'denied';
      }
    }
    this.updateVisibility();
  }

  private async queryPermissionState(name: string): Promise<PermissionState | null> {
    if (!navigator.permissions) return null;
    try {
      const status = await navigator.permissions.query({ name: name as PermissionName });
      return status.state;
    } catch {
      return null;
    }
  }

  private async recheckDevices(): Promise<void> {
    if (!this.isLoggedIn) return;

    if (this.permissions.microphone === 'granted') {
      await this.checkDeviceExists('audioinput', 'microphoneNotFound');
    }
    if (this.permissions.camera === 'granted') {
      await this.checkDeviceExists('videoinput', 'cameraNotFound');
    }
    this.updateVisibility();
  }

  private async checkDeviceExists(kind: MediaDeviceKind, notFoundFlag: 'microphoneNotFound' | 'cameraNotFound'): Promise<void> {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const found = devices.some(d => d.kind === kind);
      this.permissions[notFoundFlag] = !found;
    } catch {
      // If enumerateDevices fails, assume device exists to avoid false positives
      this.permissions[notFoundFlag] = false;
    }
  }

  dismiss(): void {
    localStorage.setItem(this.DISMISS_KEY, Date.now().toString());
    this.dismissed = true;
    this.visible = false;
  }
}
